import React, { memo, useId, useState } from 'react';
import { DraggableProvidedDragHandleProps } from '@hello-pangea/dnd';
import classNames from 'classnames';
import styles from '@lib/components/ReactHookForm/Form.module.scss';
import Typography from '@lib/components/Typography/Typography';
import MaterialIcon from '@lib/components/MaterialIcon/MaterialIcon';
import IconButton, {
  IconButtonTypes,
} from '@lib/components/IconButton/IconButton';
import DragDrop from '@lib/assets/icons/dragDrop.svg?react';

interface Props {
  children: React.ReactNode;
  dragHandleProps?: DraggableProvidedDragHandleProps | null;
  errorsCount: number;
  index: number;
  isDraggable?: boolean;
  onRemove: () => void;
  parentName: string;
  title?: string;
}

function FieldArrayItemAccordion(props: Props) {
  const {
    children,
    errorsCount,
    index,
    isDraggable,
    onRemove,
    parentName,
    title,
    dragHandleProps,
  } = props;
  const [isAccordionOpen, setAccordionOpen] = useState(true);
  const panelId = useId();
  const labelId = useId();

  const toggleAccordion = () => setAccordionOpen((prevState) => !prevState);
  const handleAccordionClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    if (event.defaultPrevented) return;
    toggleAccordion();
  };
  const accordionClassName = classNames(styles.subFieldAccordion, {
    [styles.subFieldAccordionWithErrors]: errorsCount,
    [styles.accordionClassNameDraggable]: isDraggable,
  });
  const dragProps = isDraggable && dragHandleProps ? dragHandleProps : {};
  return (
    <>
      <div
        className={classNames(styles.subFieldHead, {
          [styles.subFieldHeadOpened]: isAccordionOpen,
        })}
        data-test-id={`field-array__item-${parentName}-${index}`}
      >
        <button
          id={labelId}
          type="button"
          className={accordionClassName}
          onClick={handleAccordionClick}
          aria-expanded={isAccordionOpen}
          aria-controls={panelId}
          data-test-id={`field-array__accordion-toggle-${parentName}-${index}`}
        >
          <Typography variant="body" strong>
            {title}
          </Typography>
          {!!errorsCount && (
            <Typography variant="caption" className={styles.errorsCount}>
              {errorsCount}
            </Typography>
          )}
          <MaterialIcon
            icon="arrow_drop_down"
            className={styles.subFieldAccordionIcon}
          />
        </button>
        {isDraggable && (
          <div
            className={styles.subFieldAccordionDragDrop}
            data-test-id={`field-array__drag-handle-${parentName}-${index}`}
            {...dragProps}
          >
            <DragDrop />
          </div>
        )}
        <IconButton
          className={styles.subFieldDeleteBtn}
          data-test-id={`field-array__delete-button-${parentName}-${index}`}
          icon="delete"
          onClick={onRemove}
          symbolsOutlined
          type={IconButtonTypes.PrimaryOutlined}
        />
      </div>
      <div
        id={panelId}
        role="region"
        aria-labelledby={labelId}
        className={classNames(styles.subFieldPanel, {
          [styles.subFieldPanelOpen]: isAccordionOpen,
        })}
        inert={!isAccordionOpen}
      >
        <div className={styles.subFieldPanelInner}>
          <div className={styles.accordionContent}>{children}</div>
        </div>
      </div>
    </>
  );
}

export default memo(FieldArrayItemAccordion);
